import { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { Icon } from "@iconify/react/dist/iconify.js";
import { TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import "./DashboardInteraction.css";
import Loader from "../Components/Loader";
import formatSecondsToDuration from "../Helper/formatSecondsToDuration";

import {
  getACDDateSummary,
  getChannelAgentMonitoring,
  getChannelSummary,
} from "../Store/Actions/DashboardAction";

export default function DashboardInteraction() {
  const [ACDDateSummaryData, setACDDateSummaryData] = useState(null);

  const [channelAgentMonitoringData, setChannelAgentMonitoringData] =
    useState(null);

  const [channelSummaryData, setChannelSummaryData] = useState(null);
  const [endDate, setEndDate] = useState(moment());
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(moment());

  useEffect(() => {
    // Fetch data on first render
    fetchData(startDate, endDate);

    // Fetch data every ten seconds
    const interval = setInterval(() => {
      fetchData(startDate, endDate);
    }, 10000);

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  const columnsAgentMonitoring = [
    {
      field: "agent_name",
      flex: 1,
      headerName: "Agent Name",
    },

    {
      field: "customer",
      flex: 1,
      headerName: "Customer Name",
    },

    {
      field: "channel",
      flex: 0.7,
      headerName: "Channel",
    },

    {
      field: "date_in",
      flex: 1.25,
      headerName: "Date In",

      renderCell: (params) =>
        moment(params.row.date_in).format("yyyy-MM-DD HH:mm:ss"),
    },
  ];

  const columnsListQueueEmail = [
    {
      field: "customer_name",
      flex: 1,
      headerName: "Name",
    },

    {
      field: "date_in",
      flex: 1.25,
      headerName: "Date In",

      renderCell: (params) =>
        moment(params.row.date_in).format("yyyy-MM-DD HH:mm:ss"),
    },
  ];

  const columnsUserMonitoring = [
    {
      field: "agent_name",
      flex: 1,
      headerName: "Agent Name",
      minWidth: 120,
    },

    {
      field: "status",
      flex: 1,
      headerName: "Status",
      minWidth: 60,
    },

    {
      field: "status_duration",
      flex: 1,
      headerName: "Status Duration",
      minWidth: 120,
    },

    {
      field: "ready_time",
      flex: 1,
      headerName: "Total Ready",
      minWidth: 100,
    },

    {
      field: "aux_time",
      flex: 1,
      headerName: "Total AUX",
      minWidth: 100,
    },
  ];

  const backgroundColors = [
    "#00FF00",
    "#FF0000",
    "#FFE900",
    "#EE7314",
    "#00e1ff",
    "#f064e9",
    "#e68f0d",
    "#27cfb3",
    "#91d7ff",
    "#75da3b",
    "#ff3ab3",
    "#a626f0",
  ];

  const dataCategory = {
    labels: channelSummaryData?.category?.map(
      (category) => category.description
    ),

    datasets: [
      {
        data: channelSummaryData?.category?.map((type) => type.total),
        backgroundColor: backgroundColors,
      },
    ],
  };

  const dataChannel = {
    labels: channelSummaryData?.per_type?.map((type) => type.type_id),

    datasets: [
      {
        data: channelSummaryData?.per_type?.map((type) => type.total),
        backgroundColor: backgroundColors,
      },
    ],
  };

  const dataDetailCategory = {
    labels: channelSummaryData?.detail_category?.map(
      (detail) => detail.description
    ),

    datasets: [
      {
        data: channelSummaryData?.detail_category?.map((type) => type.total),
        backgroundColor: backgroundColors,
      },
    ],
  };

  const optionsPieChart = {
    responsive: true,

    plugins: {
      datalabels: {
        anchor: "end",
        align: "start",
        font: { size: 11 },
        color: "#393939",
        display: (context) => context.dataset.data[context.dataIndex] > 0,
      },

      legend: { position: "bottom" },
    },
  };

  const fetchData = (newStartDate, newEndDate) => {
    Promise.all([
      getChannelSummary(
        moment(newStartDate).format("YYYY-MM-DD"),
        moment(newEndDate).format("YYYY-MM-DD")
      ),

      getACDDateSummary(
        moment(newStartDate).format("YYYY-MM-DD"),
        moment(newEndDate).format("YYYY-MM-DD")
      ),

      getChannelAgentMonitoring(),
    ])
      .then(([channelResponse, acdResponse, monitoringResponse]) => {
        if (channelResponse.status) {
          setChannelSummaryData(channelResponse.data);
        } else {
          console.log("error", channelResponse.error);
        }

        if (acdResponse.status) {
          setACDDateSummaryData(acdResponse.data);
        } else {
          console.log("error", acdResponse.error);
        }

        if (monitoringResponse.status) {
          setChannelAgentMonitoringData(monitoringResponse.data);
        } else {
          console.log("error", monitoringResponse.error);
        }

        setIsLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  };

  return (
    <div className="dashboard-interaction">
      <div className="list_header">
        <div className="list_headerLeft">
          <h1>Dashboard</h1>
        </div>
      </div>

      <div className="container-dashboard-interaction">
        <section className="filters">
          <div className="form-inputs">
            <div className="filter-item date-range">
              <DatePicker
                disableFuture
                label="Start Date"
                maxDate={endDate}
                value={startDate}
                renderInput={(params) => <TextField {...params} />}
                onChange={(newValue) => setStartDate(newValue)}
              />

              <Icon color="#7E7E7E" icon="mdi:arrow-expand" width="24" />

              <DatePicker
                disableFuture
                label="End Date"
                minDate={startDate}
                value={endDate}
                renderInput={(params) => <TextField {...params} />}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </div>

            <button
              className="filter-item filter-button"
              onClick={() => fetchData(startDate, endDate)}
            >
              <Icon height="20" icon="cuida:refresh-outline" />
              Refresh
            </button>
          </div>
        </section>

        <div className="new-dashboard">
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <section className="traffic-email">
                <p className="section-name">Traffic Email</p>

                <div className="case-in-email">
                  <p className="card-title">Total In Email</p>

                  <span className="card-value">
                    {channelSummaryData?.total_in_email ?? "N/A"}
                  </span>
                </div>

                <div className="offered-email">
                  <p className="card-title">Offered Email</p>
                  <span className="card-value">
                    {channelSummaryData?.total_offered ?? "N/A"}
                  </span>
                </div>

                <div className="sl-email">
                  <p className="card-title">SL Email Within 2 Jam</p>

                  <span className="card-value">
                    {channelSummaryData?.sla_email + "%" ?? "N/A"}
                  </span>
                </div>

                <div className="total-queue-email">
                  <p className="card-title">Total Queue Email</p>

                  <span
                    className={`card-value ${channelSummaryData?.total_queue > 100 ? "high" : channelSummaryData?.total_queue > 50 ? "medium" : "low"}`}
                  >
                    {channelSummaryData?.total_queue}
                  </span>
                </div>

                <div className="aht-email">
                  <p className="card-title">AHT Email</p>

                  <span className="card-value">
                    {channelSummaryData?.aht_email ?? "N/A"}
                  </span>
                </div>

                <div className="list-queue-email">
                  <p className="card-title">List Queue Email</p>

                  <DataGrid
                    className="table"
                    columns={columnsListQueueEmail}
                    disableColumnFilter
                    disableColumnMenu
                    disableSelectionOnClick
                    hideFooter
                    rowHeight={36}
                    rows={channelSummaryData?.list_queue_email}
                    getRowId={(row) => row.ticketid}
                  />
                </div>

                <div className="login-email">
                  <p className="card-title">Login</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.login_email ?? "N/A"}
                  </span>
                </div>

                <div className="handling">
                  <p className="card-title">Handling</p>

                  <span className="card-value">
                    {channelSummaryData?.total_handling}
                  </span>
                </div>
              </section>

              <section className="traffic-voice">
                <p className="section-name">Traffic Voice</p>

                <div className="received">
                  <p className="card-title">Received</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.acdsummary[0]?.CallsOffered}
                  </span>
                </div>

                <div className="answered">
                  <p className="card-title">Answered</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.acdsummary[0]?.CallsAnswered}
                  </span>
                </div>

                <div className="abandon">
                  <p className="card-title">Abandon</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.acdsummary[0]?.CallsAbandoned}
                  </span>
                </div>

                <div className="scr">
                  <p className="card-title">SLA Call Within 20 Seconds</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.sla[0]?.SLA
                      ? ACDDateSummaryData?.sla[0]?.SLA.toFixed(1) + "%"
                      : "N/A"}
                  </span>
                </div>

                <div className="abandon-rate">
                  <p className="card-title">Abandon Rate</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.acdsummary[0]?.AbandonRate ||
                    ACDDateSummaryData?.acdsummary[0]?.AbandonRate === 0
                      ? ACDDateSummaryData?.acdsummary[0]?.AbandonRate.toFixed(
                          1
                        ) + "%"
                      : "N/A"}
                  </span>
                </div>

                <div className="aht-voice">
                  <p className="card-title">AHT Voice</p>

                  <span className="card-value">
                    {ACDDateSummaryData?.acdsummary[0]?.AvgCallDuration
                      ? formatSecondsToDuration(
                          ACDDateSummaryData?.acdsummary[0]?.AvgCallDuration
                        )
                      : "N/A"}
                  </span>
                </div>

                <div className="ringing">
                  <p className="card-title">Ringing</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.ringing ?? "N/A"}
                  </span>
                </div>

                <div className="login-voice">
                  <p className="card-title">Login</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.login_voice ?? "N/A"}
                  </span>
                </div>

                <div className="ready">
                  <p className="card-title">Ready</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.ready ?? "N/A"}
                  </span>
                </div>

                <div className="paused">
                  <p className="card-title">Paused</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.paused ?? "N/A"}
                  </span>
                </div>

                <div className="on-call">
                  <p className="card-title">On Call</p>

                  <span className="card-value">
                    {channelAgentMonitoringData?.on_call ?? "N/A"}
                  </span>
                </div>
              </section>

              <section className="ticket-monitoring">
                <div className="agent-monitoring">
                  <p className="card-title">Agent Monitoring</p>

                  <DataGrid
                    className="table"
                    columns={columnsAgentMonitoring}
                    disableColumnFilter
                    disableColumnMenu
                    disableSelectionOnClick
                    hideFooter
                    rowHeight={36}
                    rows={channelAgentMonitoringData?.agent_monitoring}
                  />
                </div>

                <div className="user-monitoring">
                  <p className="card-title">User Monitoring</p>

                  <DataGrid
                    className="table"
                    columns={columnsUserMonitoring}
                    disableColumnFilter
                    disableColumnMenu
                    disableSelectionOnClick
                    hideFooter
                    rowHeight={36}
                    rows={channelAgentMonitoringData?.user_monitoring}
                  />
                </div>
              </section>

              <section className="charts">
                <div className="channel">
                  <p className="card-title">Channel</p>

                  {channelSummaryData?.per_type.length > 0 ? (
                    <Pie
                      data={dataChannel}
                      options={optionsPieChart}
                      plugins={[ChartDataLabels]}
                    />
                  ) : (
                    <p className="card-value">N/A</p>
                  )}
                </div>

                <div className="category">
                  <p className="card-title">Category of Channel</p>

                  {channelSummaryData?.category.length > 0 ? (
                    <Pie
                      data={dataCategory}
                      options={optionsPieChart}
                      plugins={[ChartDataLabels]}
                    />
                  ) : (
                    <p className="card-value">N/A</p>
                  )}
                </div>

                <div className="detail">
                  <p className="card-title">Detail Category of Channel</p>

                  {channelSummaryData?.detail_category.length > 0 ? (
                    <Pie
                      data={dataDetailCategory}
                      options={optionsPieChart}
                      plugins={[ChartDataLabels]}
                    />
                  ) : (
                    <p className="card-value">N/A</p>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
